import { Authorization, Dialog, Fluent, resolveUrl, stringFormat, tryGetText } from "@serenity-is/corelib";
import { IdleTimeoutSettings } from "@/ServerTypes/Common/IdleTimeoutSettings";

export interface IdleTimeoutOptions {
    /** which events to consider as an activity. */
    activityEvents?: string;
    /** signout user if no activity within this duration (seconds). default is 3600 (1 hour)
     * IMPORTANT: This value should be kept in sync with the CookieTimeoutMinutes configuration
     * to prevent unexpected user logout behavior. If these values are mismatched,
     * users may be logged out earlier or later than expected.
     */
    activityTimeout?: number;
    /** optional callback that is called just before signing out */
    beforeSignout?: () => void;
    /** interval for checking timer, default is 5 seconds */
    checkInterval?: number;
    /** interval for calling keep alive url in seconds, default is 300 (5 mins) */
    keepAliveTimer?: number;
    /** keep alive url */
    keepAliveUrl?: string;
    /** url used to signout user */
    signoutUrl?: string;
    /** show warning for this number of seconds */
    warningDuration?: number;
}

const lastActivityKey = 'IdleTimeout:LastActivity';
const signoutKey = 'IdleTimeout:Signout';

export class IdleTimeout {

    declare private options: IdleTimeoutOptions;
    declare private idleTimer: number;
    declare private keepAliveTimer: number;
    declare private warningTimer: number;
    declare private remainingTimer: number;

    constructor(options: IdleTimeoutOptions) {
        this.options = Object.assign({}, IdleTimeout.defaults, options);

        localStorage.setItem(lastActivityKey, new Date().getTime().toString());
        localStorage.removeItem(signoutKey);

        let mouseX: number, mouseY: number;
        this.options.activityEvents.split(/\s+/).forEach(evt => {
            Fluent.on(document.body, evt, (e: Event) => {

                if (e.type == 'mousemove') {

                    if ((e as MouseEvent).clientX == mouseX && (e as MouseEvent).clientY == mouseY)
                        return;

                    mouseX = (e as MouseEvent).clientX;
                    mouseY = (e as MouseEvent).clientY;
                }

                if (!this.isWarningActive()) {
                    this.resetIdleTimer();
                }
            });
        });

        if (this.options.keepAliveTimer)
            this.startKeepAliveTimer();

        this.resetIdleTimer();
    }

    protected startKeepAliveTimer() {
        clearTimeout(this.keepAliveTimer);

        const keepSessionAlive = () => {
            fetch(resolveUrl(this.options.keepAliveUrl) + '?' + new Date().getTime(), {cache: "no-store"})
                .catch((e) => {
                    console.error("Failed to keep session alive", e);
                });
            this.startKeepAliveTimer();
        };

        this.keepAliveTimer = setTimeout(keepSessionAlive, this.options.keepAliveTimer * 1000);
    }

    protected isWarningActive() {
        return Fluent.isVisibleLike(document.getElementById('idle-timeout-warning'));
    }

    protected startWarningTimer() {
        this.warningTimer = setInterval(() => this.warningTimeout(), this.options.checkInterval * 1000);
    }

    protected stopWarningTimer() {
        clearInterval(this.warningTimer);
        clearInterval(this.remainingTimer);
    };

    protected warningTimeout() {
        if (this.getRemainingSeconds() < 0)
            this.signout();
    }

    protected resetIdleTimer() {
        clearTimeout(this.idleTimer);
        localStorage.setItem(lastActivityKey, new Date().getTime().toString());
        this.idleTimeout();
    };

    protected idleTimeout() {
        this.checkActivityTimeout();
        this.idleTimer = setTimeout(() => this.idleTimeout(), this.options.checkInterval * 1000);
    };

    protected checkActivityTimeout() {
        const timeLimit = parseInt(localStorage.getItem(lastActivityKey) || new Date().getTime().toString()) +
            (this.options.activityTimeout - (this.options.warningDuration || 0)) * 1000;

        if (new Date().getTime() > timeLimit) {
            if (!this.options.warningDuration) {
                this.signout();
            } else {
                this.showWarning();
                this.startWarningTimer();
            }
        } else if (localStorage.getItem(signoutKey)) {
            this.signout();
        } else if (this.isWarningActive()) {
            this.closeWarning();
            this.stopWarningTimer();
        }
    }

    protected signout() {
        localStorage.setItem(signoutKey, "1");
        clearTimeout(this.keepAliveTimer);
        this.stopWarningTimer();
        this.closeWarning();
        this.options.beforeSignout && this.options.beforeSignout();
        if (this.options.signoutUrl)
            window.location.href = resolveUrl(this.options.signoutUrl);
    }

    protected showWarning() {
        let warningDialog = Dialog.getInstance(document.getElementById("idle-timeout-warning"));
        if (!warningDialog) {
            warningDialog = new Dialog({
                autoDispose: true,
                autoOpen: true,
                title: tryGetText("Controls.IdleTimeout.WarningTitle") ?? "Warning",
                buttons: [{
                    text: tryGetText("Controls.IdleTimeout.SignoutButton") ?? "Sign Out",
                    cssClass: "btn btn-danger",
                    click: () => this.signout()
                }, {
                    text: tryGetText("Controls.IdleTimeout.KeepAliveButton") ?? "Stay Signed In",
                    cssClass: "btn btn-primary",
                    click: () => warningDialog.close()
                }],
                element: el => {
                    el.append(<>
                            <p>{tryGetText("Controls.IdleTimeout.WarningMessage") ?? "Your session is about to expire."}</p>
                        <p class="idle-timeout-text"></p>
                        <div class="progress">
                    <div id="idle-timeout-progress" class="progress-bar progress-bar-striped active" role="progressbar"
                    style="min-width: 15px; width: 100%;">
                        </div>
                        </div>
                        </>)
                },
                onClose: () => {
                    clearTimeout(this.warningTimer);
                    clearTimeout(this.remainingTimer);
                    this.resetIdleTimer();
                }
            });
        }
        warningDialog.getDialogNode().id = "idle-timeout-warning";

        this.countdownDisplay();
    }

    protected closeWarning() {
        Dialog.getInstance(document.getElementById('idle-timeout-warning'))?.close();
    }

    protected getRemainingSeconds() {
        const timeLimit = parseInt(localStorage.getItem(lastActivityKey) || new Date().getTime().toString()) +
            (this.options.activityTimeout * 1000);
        return Math.ceil(((timeLimit - new Date().getTime()) / 1000));
    }

    protected countdownDisplay() {
        clearTimeout(this.remainingTimer);
        const update = () => {
            const remainingSeconds = Math.max(this.getRemainingSeconds(), 1);
            const percentLeft = 100 * Math.max(1, remainingSeconds) / (this.options.warningDuration || 1);
            let progress = document.getElementById('idle-timeout-progress');
            progress && (progress.style.width = percentLeft + '%');
            let secs = document.getElementById("idle-timeout-warning").querySelector(".idle-timeout-text");
            secs && (secs.textContent = stringFormat(tryGetText("Controls.IdleTimeout.CountdownMessage") ??
                "You will be logged off in {0} seconds due to inactivity.", remainingSeconds));
        };
        this.remainingTimer = setInterval(update, 500);
        update();
    };

    static settings: IdleTimeoutSettings = JSON.parse(document.getElementById("IdleTimeoutSettings")?.textContent || "{}");

    static defaults: IdleTimeoutOptions = {
        activityEvents: 'click keyup mouseup mousemove scroll touchend touchmove wheel mousewheel',
        activityTimeout: IdleTimeout.settings.CookieTimeoutMinutes * 60,
        checkInterval: IdleTimeout.settings.CheckIntervalSeconds,
        keepAliveTimer: IdleTimeout.settings.KeepAliveTimerMinutes * 60,
        keepAliveUrl: '~/Account/KeepAlive',
        signoutUrl: '~/Account/Signout',
        warningDuration: IdleTimeout.settings.WarningDurationSeconds
    }
}

Fluent.ready(() => {
    if (IdleTimeout.settings.Enabled !== true)
        return;

    const meta = Fluent(document.querySelector('meta[name=username]'));
    if (meta.attr('content') || (!meta.length && Authorization.isLoggedIn))
        new IdleTimeout({});
});